// Ported from qr-phone-frontend src/app/services/helper.service.ts (v3.2.0.0).
// RTDB paths and status strings are protocol: the backend, the mobile apps and the
// history views read these exact shapes. moment() usages were replaced with Date
// equivalents (same stored values).
import { ref, update, child, get } from 'firebase/database'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { database, firestore } from './firebase'
import { globals } from './globals'

// FUNCTION TO UPDATE CALLSTATUS FIELD AND ADD A STATUS TO THE CALLSTATUSES ARRAY IF IT'S NOT ALREADY THERE.
export const updateCallStatus = async (callId: any, newStatus: string, otherFields?: any) => {
  try {
    if (callId && typeof callId !== 'undefined' && callId !== '') {
      const databaseRef = ref(database, 'call')
      const callRef = child(databaseRef, callId)

      if (callRef) {
        // FIRSTLY, CHECK IF THE NEW STATUS IS DIFFERENT FROM THE PREVIOUS STATUS
        const snapshot = await get(child(callRef, 'callstatus'))
        const previousStatus = snapshot.val()

        if (otherFields && typeof otherFields === 'object') {
          await update(callRef, { callstatus: newStatus, ...otherFields })
        } else {
          await update(callRef, { callstatus: newStatus })
        }

        if (newStatus !== previousStatus) {
          // THE STATUS HAS CHANGED, ADD THE NEW STATUS TO THE CALLSTATUSES ARRAY
          const callStatusRef = child(callRef, 'callstatuses')
          const callstatusesSnapshot = await get(callStatusRef)
          if (!callstatusesSnapshot.exists()) {
            if (otherFields && typeof otherFields === 'object') {
              await update(callRef, { callstatuses: [newStatus], ...otherFields })
            } else {
              await update(callRef, { callstatuses: [newStatus] })
            }
          } else {
            const statuses = callstatusesSnapshot.val()
            if (!statuses.includes(newStatus)) {
              statuses.push(newStatus)
              if (otherFields && typeof otherFields === 'object') {
                await update(callRef, { callstatuses: statuses, ...otherFields })
              } else {
                await update(callRef, { callstatuses: statuses })
              }
            }
          }
        }
      }
    } else {
      console.log('update invalid parameter', callId, newStatus)
    }
  } catch (error) {
    console.error('Error updating callstatus and callstatuses:', error)
  }
}

// SAME, BUT SCOPED TO THE PER-STAFF NODE (notified_user_id/<id> OR defaultstaffnotified).
export const updateCallStatusByUserId = async (callId: any, newStatus: string, userId: any) => {
  try {
    if (typeof callId !== 'undefined' && callId !== '') {
      const databaseRef = ref(database, 'call')

      if (callId === 'undefined' || newStatus === 'undefined' || callId === '' || newStatus === '') {
        console.log('update user_id invalid parameter', callId, newStatus)
        return
      }

      const childRef = child(databaseRef, callId)
      const notifiedRef = child(childRef, 'notified_user_id')
      let updateFieldRef

      const defaultNotified = (globals as any)?.isThisDefaultStaffNotified
      if (defaultNotified === true || defaultNotified === 'true') {
        updateFieldRef = child(childRef, 'defaultstaffnotified')
      } else {
        updateFieldRef = child(notifiedRef, `${userId}`)
      }

      await update(updateFieldRef, { callstatus: newStatus })

      const callStatusRef = child(updateFieldRef, 'callstatuses')
      const callstatusesSnapshot = await get(callStatusRef)

      if (!callstatusesSnapshot.exists()) {
        await update(updateFieldRef, { callstatuses: [newStatus] })
      } else {
        const statuses = callstatusesSnapshot.val()
        if (!statuses.includes(newStatus)) {
          statuses.push(newStatus)
          await update(updateFieldRef, { callstatuses: statuses })
        }
      }
    } else {
      console.log('update user_id invalid parameter', callId, newStatus)
    }
  } catch (error) {
    console.error('Error updating callstatus and callstatuses2:', error)
  }
}

// UPDATE ARBITRARY COLUMNS OF THE CALL RECORD IN THE REALTIME DATABASE.
export const updateCallRealTime = async (call_id: any, column: any) => {
  try {
    if (typeof column === 'object' && typeof call_id !== 'undefined') {
      const databaseRef = ref(database, 'call')
      const callRef = child(databaseRef, call_id)
      await update(callRef, column)
    } else {
      console.log('update invalid parameter')
    }
  } catch (e) {
    console.error('updateCallRealTime error is, ', e)
  }
}

// COPY THE FINISHED CALL RECORD FROM RTDB INTO FIRESTORE (ONCE).
export const insertCallRecordIntoFirestore = async (call_id: any) => {
  try {
    if (!call_id || typeof call_id !== 'string' || call_id.trim() === '') {
      throw new Error('Invalid call_id. Ensure it is a non-empty string.')
    }
    const callFirestoreDoc = doc(firestore, 'call', call_id)

    const docSnap = await getDoc(callFirestoreDoc)
    if (!docSnap.exists()) {
      try {
        const databaseRef = ref(database, 'call')
        const callRef = child(databaseRef, call_id)

        const snapshot = await get(callRef)
        const callData = snapshot.val()
        const time = callData.timecallinitiated || new Date()
        callData.timecallinitiated = new Date(time)

        try {
          await setDoc(callFirestoreDoc, callData)
          return
        } catch (e) {
          console.error('Error storing call record:', e)
        }
      } catch (e) {
        console.error('Error inserting call details:', e)
      }
    }
  } catch (error) {
    console.error('Error inserting call detail to firestore:', error)
  }
}

// CONVERT SECONDS TO A {HH:}MM:SS LABEL.
export const convertTimerFormat = (time: any) => {
  const h = Math.floor(time / 3600)
  const m = Math.floor((time - h * 3600) / 60)
  const s = time % 60

  const hours = String(h).padStart(2, '0')
  const minutes = String(m).padStart(2, '0')
  const seconds = String(s).padStart(2, '0')
  return h > 0 ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`
}
